import { Injectable } from '@angular/core'
import { Observable, concatMap, from, last, scan } from 'rxjs'
import { TasksService } from './tasks.service'
import { Page } from '../../models/util.model'
import { NgNotificationServiceInterface } from '../../models/notification.model'
import { Task } from './models/task.entity'
import {
  parseTaskNotification,
  TaskNotificationCode,
} from './libs/task-notification.lib'
import { TaskSearchParams } from './models/task-search.model'
import { uniqBy } from 'lodash'

@Injectable({
  providedIn: 'root',
})
export class TasksRepository {
  constructor(
    private tasksService: TasksService,
    private ngNotificationService?: NgNotificationServiceInterface,
  ) {}

  /**
   * Search supplier orders and external data
   * @param searchParams - the search params
   * @param fields - the fields
   * @returns the observable for search supplier orders
   */
  searchTasks$(
    searchParams: TaskSearchParams,
    returnAll?: boolean,
  ): Observable<Page<Task>> {
    const needsPagination =
      searchParams.limit !== undefined &&
      searchParams.limit > this.tasksService.limit

    return needsPagination
      ? this._paginateTasks$(searchParams)
      : this._searchTasks$(searchParams, returnAll)
  }

  /**
   * Search supplier orders with relative external data
   * @param searchParams - the search params
   * @param fields - the goods-returns fields
   * @param listingData - the already loaded listing data
   * @returns the observable<Page<Task>> for search goods-returns
   */
  private _searchTasks$(
    searchParams: TaskSearchParams,
    returnAll?: boolean,
  ): Observable<Page<Task>> {
    return this.tasksService.search$(searchParams, returnAll).pipe()
  }

  /**
   * Search more than 200 goods-returns with relative external data
   * @param searchParams - the search params
   * @param fields - the goods-returns fields
   * @param listingData - the already loaded listing data
   * @returns the observable<Page<Task>> for search goods-returns
   */
  private _paginateTasks$(
    searchParams: TaskSearchParams,
  ): Observable<Page<Task>> {
    const totalCount = searchParams.limit || this.tasksService.limit
    const pagesCount = Math.ceil(totalCount / this.tasksService.limit)

    return from(
      Array.from({ length: pagesCount }, (_, i) => ({
        ...searchParams,
        limit: this.tasksService.limit,
        offset: i * this.tasksService.limit,
      })),
    ).pipe(
      concatMap((pageParams) => this._searchTasks$(pageParams)),
      scan<Page<Task>, Page<Task>>(
        (acc, current) => ({
          totalCount: uniqBy(
            [...(acc.data || []), ...(current.data || [])],
            '_id',
          ).length,
          data: uniqBy([...(acc.data || []), ...(current.data || [])], '_id'),
        }),
        { totalCount: 0, data: [] } as Page<Task>,
      ),
      last(),
    )
  }

  notify(options: { code?: TaskNotificationCode; message?: string }): void {
    this.ngNotificationService?.show(
      options.message
        ? { title: 'Error', message: options.message, color: 'error' }
        : parseTaskNotification(options.code),
    )
  }
}
